<template>
    <div class="widget-real-time-settings">
        <div class="flex flex-col gap-3">
            <div
                v-for="(setting, key) in visibleSettings"
                :key="key"
                class="flex flex-col gap-3"
            >
                <VcCheckbox
                    v-model="setting.model.value"
                    class="whitespace-normal"
                >
                    {{ t(setting.label) }}
                </VcCheckbox>
                <div
                    v-if="key === 'showLoggedOutUsers' && setting.model.value"
                    class="flex flex-col gap-3 pl-6"
                >
                    <VcCheckbox
                        v-model="showRecentlyLoggedOutModel"
                        class="whitespace-normal"
                    >
                        {{ t('settings.extension.recentlyLoggedOut') }}
                    </VcCheckbox>
                    <VcTimeInput
                        v-if="showRecentlyLoggedOutModel"
                        v-model="recentlyLoggedOutMaxDurationModel"
                        :min-value="0"
                        :step="1"
                        class="input-number"
                        max-width="200px"
                        color="primary"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { Settings } from '@/types/dashboard'
import { computed, WritableComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { useVModel } from '@vueuse/core'
import { VcCheckbox, VcTimeInput } from '@voicenter-team/voicenter-ui-plus'
import { isExtensionCardStatisticsWidgetByTypeId, isTableWidgetByTypeId } from '@/helpers/widgetUtils'

const { t } = useI18n()

interface RealtimeSetting {
    model: WritableComputedRef<boolean | undefined>
    label: string,
    visible: boolean
}

const props = defineProps<{
    modelValue: Settings
    dataType: number
}>()

const emit = defineEmits<{
    (e: 'update:modelValue', value: Settings): void
}>()

const model = useVModel(props, 'modelValue', emit, {
    eventName: 'update:modelValue',
})


const settings = {
    showLoggedOutUsers: {
        model: computed({
            get: () => model.value.showLoggedOutUsers ?? false,
            set: (val) => {
                model.value = {
                    ...model.value,
                    showLoggedOutUsers: val
                }
            },
        }),
        label: 'settings.extension.logout',
        visible: true
    },
    showLoggedOutUsersTime: {
        model: computed({
            get: () => model.value.showLoggedOutUsersTime ?? false,
            set: (val) => {
                model.value = {
                    ...model.value,
                    showLoggedOutUsersTime: val
                }
            },
        }),
        label: 'settings.extension.logoutTime',
        visible: true
    },
    resetIdleTime: {
        model: computed({
            get: () => model.value.resetIdleTime ?? false,
            set: (val) => {
                model.value = {
                    ...model.value,
                    resetIdleTime: val
                }
            },
        }),
        label: 'settings.extension.resetIdleTimeOnly',
        visible: true
    },
    resetStatusTimeOnCallUpdate: {
        model: computed({
            get: () => model.value.resetStatusTimeOnCallUpdate ?? false,
            set: (val) => {
                model.value = {
                    ...model.value,
                    resetStatusTimeOnCallUpdate: val
                }
            },
        }),
        label: 'settings.extension.resetStatusTimeOnCallUpdate',
        visible: true
    },
    showStatusTagWhenOnCall: {
        model: computed({
            get: () => model.value.showStatusTagWhenOnCall ?? false,
            set: (val) => {
                model.value = {
                    ...model.value,
                    showStatusTagWhenOnCall: val
                }
            },
        }),
        label: 'settings.extension.showStatusTagWhenOnCall',
        visible: isTableWidgetByTypeId(props.dataType)
    },
    showTotalCallsSummary: {
        model: computed({
            get: () => model.value.showTotalCallsSummary ?? false,
            set: (val) => {
                model.value = {
                    ...model.value,
                    showTotalCallsSummary: val
                }
            },
        }),
        label: 'settings.extension.showTotalCallsSummary',
        visible: isExtensionCardStatisticsWidgetByTypeId(props.dataType)
    }
}

const visibleSettings = computed(() => {
    return Object.entries(settings).reduce<Record<string, RealtimeSetting>>((acc, [ key, setting ]) => {
        if (setting.visible) {
            acc[key] = setting
        }
        return acc
    }, {})
})

const showRecentlyLoggedOutModel = computed({
    get: () => model.value.showRecentlyLoggedOut,
    set: (val) => {
        model.value = {
            ...model.value,
            showRecentlyLoggedOut: val
        }
    },
})

const recentlyLoggedOutMaxDurationModel = computed({
    get: () => model.value.recentlyLoggedOutMaxDuration ?? 0,
    set: (val) => {
        model.value = {
            ...model.value,
            recentlyLoggedOutMaxDuration: val
        }
    },
})
</script>
